/*eslint max-lines-per-function: ["error", 280]*/
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import ConfirmationButton from '../../../app/components/ConfirmationButton';
import FormRow from '../../../app/components/FormRow';
import {
  CreateUpdateAttachmentDto,
  CreateUpdateExamResultDto,
  GetAttachmentDto,
  GetExamResultDto,
  GetLastApprovalDateDto,
  ValidationStatus,
} from '../../../common/snrwbCore/autogenerated/snrwbApiClient';
import * as ExamResultContext from '../../../common/snrwbCore/contexts/ExamResultContext';
import AssociatedDocuments from '../AssociatedDocuments/AssociatedDocuments';
import { AssociateDocuments } from '../AssociatedDocuments/AssociateDocumentsTypes';
import { AddAttachment } from '../Attachments/AddAttachment';
import { Attachment } from '../Attachments/Attachment';
import momencik from '../../../common/momencik';
import { Role } from '../../../common/snrwbCore/authorization/snrwbAuthorizationRoles';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNavigation } from '../../../common/navigation';
import { useSnrwbCore } from '../../../common/hooks/useSnrwbCore';
import { useNotifications } from '../../../common/hooks/useNotifications';

const ResultForm = (props: {
  value: GetExamResultDto;
  publishAuthorized: boolean;
  attachment: AssociateDocuments<GetAttachmentDto, CreateUpdateAttachmentDto>;
  changeDescription: (description?: string) => void;
  changeVerification: () => void;
}) => (
  <Form className="d-grid">
    <FormRow
      controlId="description"
      label={props.value.description ? 'Informacja dla WINB' : ''}
      static
    >
      <Row>
        <Col>{props.value.description}</Col>
        {props.value.approved && (
          <Col md={5} className="float-end">
            {props.value.approved && (
              <AssociatedDocuments
                name="Załącznik"
                align="end"
                oneDocumentOnly={true}
                readonly={
                  !props.publishAuthorized ||
                  props.value.publicationDate !== undefined
                }
                createModalType={AddAttachment}
                presentModalType={Attachment}
                docs={props.attachment}
                className="float-end"
              />
            )}
            {!props.value.publicationDate &&
              (props.value.verified ? (
                <ConfirmationButton
                  confirmation="Czy na pewno cofnąć weryfikację?"
                  onOK={props.changeVerification}
                  rolesAllOf={[Role.ExamsPublishing, Role.ExamsEdit]}
                  variant="outline-secondary"
                  className="float-end me-1"
                >
                  Cofnij weryfikację
                </ConfirmationButton>
              ) : (
                <ConfirmationButton
                  confirmation="Czy na pewno próbka została zweryfikowana?"
                  onOK={props.changeVerification}
                  rolesAllOf={[Role.ExamsPublishing, Role.ExamsEdit]}
                  variant="outline-secondary"
                  className="float-end me-1"
                >
                  Zweryfikuj
                </ConfirmationButton>
              ))}
            {props.value.description && (
              <ConfirmationButton
                confirmation="Czy na pewno zmienić informację?"
                onOK={props.changeDescription}
                rolesAllOf={[Role.ExamsPublishing, Role.ExamsEdit]}
                askForAReason
                reasonText="Informacja dla WINB"
                reason={props.value.description}
                variant="outline-secondary"
                className="float-end me-1"
              >
                Zmień
              </ConfirmationButton>
            )}
          </Col>
        )}
      </Row>
    </FormRow>
  </Form>
);

const PublishButtons = (props: {
  published: boolean;
  unpublishable: boolean;
  mayBePublished: () => Promise<boolean>;
  onPublish: () => void;
  onRevertPublish: () => void;
  onPdf: () => void;
}) => (
  <>
    <Button onClick={props.onPdf} variant="outline-secondary" className="me-1">
      Podgląd PDF
    </Button>
    {!props.unpublishable && (
      <>
        {!props.published ? (
          <ConfirmationButton
            roleAnyOf={[Role.ExamsPublishing]}
            onValidation={props.mayBePublished}
            confirmation="Czy na pewno publikować wynik?"
            onOK={props.onPublish}
            variant="outline-success"
          >
            Publikuj
          </ConfirmationButton>
        ) : (
          <ConfirmationButton
            confirmation="Czy cofnąć publikację wyniku?"
            roleAnyOf={[Role.ExamsPublishing]}
            onOK={props.onRevertPublish}
            variant="outline-danger"
          >
            Cofnij publikację
          </ConfirmationButton>
        )}
      </>
    )}
  </>
);

export default (props: {
  value: GetExamResultDto;
  editAuthorized: boolean;
  publishAuthorized: boolean;
  attachment: AssociateDocuments<GetAttachmentDto, CreateUpdateAttachmentDto>;
  className?: string;
  onUpdate: (dto: CreateUpdateExamResultDto) => void;
  onApprove: () => void;
  onRevertApprove: (reason?: string) => void;
  onSendToNB: (reason?: string) => void;
  sendToNBReason: string;
  mayBeApproved: () => Promise<ValidationStatus>;
  mayBePublished: () => Promise<ValidationStatus>;
  onDelete: () => void;
  onPublish: () => void;
  onRevertPublish: () => void;
  onPdf: () => void;
  onMarkAsUnpublishable: () => void;
  onRevertMarkAsUnpublishable: () => void;
}) => {
  const nav = useNavigation();
  const snrwb = useSnrwbCore();
  const notifications = useNotifications();

  const [header, setHeader] = useState<string>();
  const [headerMin, setHeaderMin] = useState<string>();
  const [headerMax, setHeaderMax] = useState<string>();
  const [showAlert, setShowAlert] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [lastApprovalDate, setLastApprovalDate] =
    useState<GetLastApprovalDateDto>();

  const approved = props.value.approved;
  const negative = props.value.negative;
  const published =
    props.value.publicationDate !== undefined &&
    props.value.publicationDate !== null;
  const unpublishable = !!props.value.unpublishableDate;

  const setNegative = () => {
    const dto = ExamResultContext.toCuDto(props.value);
    dto.negative = !dto.negative;
    props.onUpdate(dto);
  };

  const changeDescription = (description?: string) => {
    const dto = ExamResultContext.toCuDto(props.value);
    if (dto.description !== description) {
      dto.description = description || '';
      props.onUpdate(dto);
    }
  };

  const changeVerification = () => {
    const dto = ExamResultContext.toCuDto(props.value);
    dto.verified = !dto.verified;
    props.onUpdate(dto);
  };
  const validate = async (method: () => Promise<ValidationStatus>) => {
    const status = await method();
    if (status.state === 'ok') {
      return true;
    } else {
      setShowAlert(true);
      setErrors(status.errors);
      return false;
    }
  };
  const checkAttachment = async () => {
    if (props.attachment.documents.length > 0) {
      setShowAlert(true);
      setErrors([
        'Przed cofnięciem przekazania do publikacji należy usunąć załącznik.',
      ]);
      return false;
    }
    return true;
  };
  const isNotifiedUnit = async () => {
    if (!props?.value?.sample?.notifiedUnit) {
      setShowAlert(true);
      setErrors(['Brak wskazanej jednostki notyfikowanej']);
      return false;
    }
    return true;
  };
  const mountedRef = useRef(false);
  const safe = (set: () => void) => {
    if (mountedRef.current) {
      set();
    }
  };
  const refresh = useCallback(() => {
    snrwb.examResultsHistory
      .getLastApprovalDate(props.value.id)
      .then(date => {
        safe(() => setLastApprovalDate(date));
      })
      .catch(() => notifications.badLink());
  }, [snrwb, notifications, props.value.id]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [props.value.id, snrwb.examResultsHistory, refresh]);

  useEffect(() => {
    if (props.value.publicationDate) {
      setHeader(
        `Wynik ${
          props.value.negative ? 'negatywny' : 'pozytywny'
        } opublikowany ${momencik(props.value.publicationDate)} (numer: ${
          props.value.yearOfCommission + '/' + props.value.yearOrderNo
        })`,
      );
    } else {
      setHeader(
        `${
          props.value.approved ? 'Przekazany' : 'Nieprzekazany'
        } do publikacji (wynik ${
          props.value.negative ? 'negatywny' : 'pozytywny'
        })`,
      );
    }
    props.value.approvedDate &&
      setHeaderMin(
        `Pierwsze przekazanie do publikacji ${momencik(
          props.value.approvedDate,
        )}`,
      );

    lastApprovalDate &&
      lastApprovalDate.date &&
      setHeaderMax(
        `Ostatnie przekazanie do publikacji ${momencik(lastApprovalDate.date)}`,
      );
  }, [props.value, lastApprovalDate]);

  return (
    <Alert
      variant={negative ? 'danger' : 'success'}
      className={props.className}
    >
      <Alert.Heading className="d-flex">
        <div className="flex-grow-1 align-self-center">{header}</div>

        {(props.editAuthorized || props.publishAuthorized) && (
          <div className="text-end">
            {approved ? (
              <>
                {!published && !unpublishable && (
                  <ConfirmationButton
                    confirmation="Czy na pewno cofnąć przekazanie wyniku do publikacji?"
                    onOK={props.onRevertApprove}
                    rolesAllOf={[Role.ExamsPublishing, Role.ExamsRevoke]}
                    askForAReason
                    reasonNotEmpty
                    reasonText="Informacja dla WINB"
                    reason={props.value.description}
                    variant="outline-danger"
                    className="mx-1"
                    onValidation={checkAttachment}
                  >
                    Cofnij przekazanie do publikacji
                  </ConfirmationButton>
                )}
                <PublishButtons
                  published={published}
                  unpublishable={unpublishable}
                  mayBePublished={() => validate(props.mayBePublished)}
                  onPublish={props.onPublish}
                  onRevertPublish={props.onRevertPublish}
                  onPdf={props.onPdf}
                />
                {negative && (
                  <ConfirmationButton
                    confirmation="Czy na pewno chcesz wysłać do NB informację o opublikowaniu wyników badania próbki?"
                    onOK={props.onSendToNB}
                    reason={props.sendToNBReason}
                    rolesAllOf={[Role.ExamsNotyfication]}
                    askForAReason
                    reasonNotEmpty
                    reasonText="Informacja dla NB"
                    variant="outline-danger"
                    className="mx-1"
                    rows={3}
                    onValidation={isNotifiedUnit}
                  >
                    Przekaż do NB
                  </ConfirmationButton>
                )}
                {!published && (
                  <div className="m-1 text-end">
                    {!props.value.unpublishableDate ? (
                      <ConfirmationButton
                        roleAnyOf={[Role.ExamsPublishing, Role.ExamsRevoke]}
                        confirmation="Czy na pewno oznaczyć wynik jako niemożliwy do publikacji?"
                        onOK={props.onMarkAsUnpublishable}
                        variant="outline-danger"
                      >
                        Oznacz jako niemożliwe do publikacji
                      </ConfirmationButton>
                    ) : (
                      <ConfirmationButton
                        confirmation="Czy cofnąć oznaczenie o braku możliwości publikacji?"
                        roleAnyOf={[Role.ExamsPublishing, Role.ExamsRevoke]}
                        onOK={props.onRevertMarkAsUnpublishable}
                        variant="outline-success"
                      >
                        Wycofaj oznaczenie o braku możliwości publikacji
                      </ConfirmationButton>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <ConfirmationButton
                  confirmation="Czy na pewno usunąć wynik?"
                  roleAnyOf={[Role.ExamsEdit]}
                  onOK={props.onDelete}
                  variant="outline-danger"
                >
                  Usuń
                </ConfirmationButton>
                <Button
                  variant="outline-secondary"
                  className="mx-1"
                  onClick={setNegative}
                >
                  {negative ? 'Pozytywny' : 'Negatywny'}
                </Button>
                <ConfirmationButton
                  confirmation="Czy na pewno przekazać wynik badania próbki do publikacji?"
                  onOK={props.onApprove}
                  variant="outline-primary"
                  onValidation={() => validate(props.mayBeApproved)}
                >
                  Przekaż do publikacji
                </ConfirmationButton>
              </>
            )}
            <ValidationAlert
              show={showAlert}
              modal
              onHide={() => setShowAlert(false)}
              errors={errors}
              buttonText="Zamknij"
            />
          </div>
        )}
      </Alert.Heading>
      <div className="d-flex">
        <div className="flex-grow-1 align-self-center">
          <div>{headerMin}</div>
          <div>{headerMax}</div>
        </div>
        <Button
          className="ms-1"
          variant="outline-primary"
          onClick={() => nav.timelineExamResult(props.value.sample.id)}
        >
          Oś czasu
        </Button>
      </div>
      <hr />
      <ResultForm
        value={props.value}
        publishAuthorized={props.publishAuthorized}
        attachment={props.attachment}
        changeDescription={changeDescription}
        changeVerification={changeVerification}
      />
    </Alert>
  );
};
